import { Component, inject, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';

import { CeRequestStateService } from '../../services/ce-request-state.service';
import { FlowStep, getExistingProjectSteps } from '../../data/flow-steps';
import { QuestionnaireApiService } from '../../services/questionnaire-api.service';
import { ProjectStageService } from '../../services/project-stage.service';
import { DictionariesApiService } from '../../services/dictionaries-api.service';
import { HttpNotificationService } from '../../services/http-notification.service';
import { ProjectCustomDropdownDto, ProjectsApiService, UpdateProjectCommand } from '../../services/projects-api.service';
import { CurrentUserService } from '../../services/current-user.service';
import { LoggerService } from '../../services/logger.service';
import { firstValueFrom } from 'rxjs';

const EMPTY_OPTIONS: string[] = [];
const SUBMITTED_STATUS_TYPE = 1;
const ISSUED_STATUS_TYPE = 4;

@Component({
  selector: 'app-existing-project',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatAutocompleteModule, MatTooltipModule],
  templateUrl: './existing-project.html',
  styleUrl: './existing-project.scss',
})
export class ExistingProjectComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly state = inject(CeRequestStateService);
  private readonly dictionariesApi = inject(DictionariesApiService);
  private readonly projectsApi = inject(ProjectsApiService);
  private readonly notifications = inject(HttpNotificationService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly logger = inject(LoggerService);
  private readonly questionnaireApi = inject(QuestionnaireApiService);
  private readonly stageService = inject(ProjectStageService);

  protected readonly steps = signal<readonly FlowStep[]>([]);
  protected readonly projectId = signal<number | null>(null);
  protected readonly projectStatusType = signal<number | null>(null);
  protected readonly isSubmittedStatus = computed(() => {
    const status = this.projectStatusType();
    return status === SUBMITTED_STATUS_TYPE || status === ISSUED_STATUS_TYPE;
  });
  protected readonly isProjectManagementTypeReadonly = computed(() => {
    if (this.isSubmittedStatus()) {
      return true;
    }
    const isCeEngineer = this.currentUserService.hasAnyRole(['ce_engineer', 'ce_engineers']);
    if (!isCeEngineer) {
      return false;
    }
    // CE Engineer cannot edit in any status except Draft (null = new project = draft)
    const status = this.projectStatusType();
    return status !== null && status !== 0;
  });
  private readonly originalPmtValue = signal<string>(this.state.draftForm().projectManagementType || '');
  private readonly hasPmtChanged = computed(() => !!this.projectId() && this.formData().projectManagementType !== this.originalPmtValue());
  private pmtPendingCallback: (() => Promise<void>) | null = null;
  protected readonly showPmtConfirmModal = signal(false);
  protected readonly autoDownloadAnswers = signal(false);
  protected readonly isDownloadingAnswers = signal(false);
  protected readonly canAccessResponse = computed(() => {
    const isProjectEngineer = this.currentUserService.hasAnyRole(['project_engineer', 'project_engineers']);
    if (!isProjectEngineer) {
      return true;
    }

    const status = this.projectStatusType();
    return typeof status === 'number' && status >= SUBMITTED_STATUS_TYPE;
  });

  protected readonly shouldShowSaveDraft = computed(() => {
    // Save Draft is only for initial project creation.
    return !this.projectId();
  });

  protected readonly shouldShowSave = computed(() => {
    // Save is available for all statuses when project already exists.
    return !!this.projectId();
  });

  protected projectTitleOptions: string[] = [];
  protected estimateTitleOptions: string[] = [];
  protected projectNumberOptions: string[] = [];
  protected projectCodeOptions: string[] = [];
  protected estimateRequestTypes: string[] = [];
  protected projectManagementTypes: string[] = [];
  protected cpdepPhases: string[] = [];
  protected designProgressOptions: string[] = [];
  protected areaOptions: string[] = [];
  protected unitOptions: string[] = [];
  protected ibpOptions: string[] = [];
  protected taYearOptions: string[] = [];
  protected readonly showAllAutocomplete = signal<Record<string, boolean>>({});

  protected formData = signal({
    estimateYear: this.state.draftForm().estimateYear || '',
    sequenceNumber: this.state.draftForm().sequenceNumber || '',
    projectTitle: this.state.draftForm().projectTitle || '',
    estimateTitle: this.state.draftForm().estimateTitle || '',
    projectNumber: this.state.draftForm().projectNumber || '',
    sequenceTitle: this.state.draftForm().sequenceTitle || '',
    projectCode: this.state.draftForm().projectCode || '',
    estimateRequestType: this.state.draftForm().estimateRequestType || '',
    projectManagementType: this.state.draftForm().projectManagementType || '',
    cpdepPhase: this.state.draftForm().cpdepPhase || '',
    designProgress: this.state.draftForm().designProgress || '',
    anticipatedCompletionDate: this.state.draftForm().anticipatedCompletionDate || '',
    preliminaryCostDeadline: this.state.draftForm().preliminaryCostDeadline || '',
    area: this.state.draftForm().area || '',
    unit: this.state.draftForm().unit || '',
    ibp: this.state.draftForm().ibp || '',
    taYear: this.state.draftForm().taYear || '',
  });
  protected editingFields = signal<Record<string, boolean>>({});
  protected searchAttempted = signal(false);
  protected detailsAttempted = signal(false);
  protected searchError = signal<string | null>(null);
  protected searchNotFound = signal(false);

  constructor() {
    void this.currentUserService.load();
    this.state.setDetailsRoute('/existing-project');
    const paramId = this.route.snapshot.queryParamMap.get('projectId');
    const hasProjectId = !!paramId;
    this.steps.set(
      getExistingProjectSteps(
        this.state.flowProgress().questionnaireVisited || hasProjectId,
        this.state.flowProgress().responseVisited || hasProjectId,
        this.canAccessResponse(),
      ),
    );
    void this.stageService.ensureLoaded().catch(error => this.logger.warn('Failed to load project stages', error));
    void this.loadCpdepPhases();
    void this.loadDesignProgresses();
    void this.loadEstimateRequestTypes();
    void this.loadIbpYears();
    void this.loadProjectManagementTypes();
    void this.loadTurnaroundYears();
    void this.loadUnits();
    void this.loadAreas();
    if (paramId) {
      const id = Number(paramId);
      this.projectId.set(id);
      void this.loadProjectById(id);
    }
  }

  private async loadProjectById(id: number): Promise<void> {
    try {
      const dto = await firstValueFrom(this.projectsApi.getProjectById(id));
      this.projectStatusType.set(dto.projectStatusType ?? 0);

      // Keep shared draft in sync when user opens details directly,
      // so save/update payload includes team fields.
      this.state.draftForm.update(d => ({
        ...d,
        projectTeam: dto.projectTeam ?? d.projectTeam,
        afeNumber: dto.afeNumber ?? d.afeNumber,
        isNewProject: dto.isNewProject ?? false,
      }));

      this.formData.set({
        estimateYear: String(dto.estimateYear ?? ''),
        sequenceNumber: String(dto.sequenceNumber ?? ''),
        projectTitle: dto.projectTitle ?? '',
        estimateTitle: dto.estimateTitle ?? '',
        projectNumber: dto.projectNumber ?? '',
        sequenceTitle: `${dto.estimateYear ?? ''}-${dto.sequenceNumber ?? ''}`,
        projectCode: dto.projectCode ?? '',
        estimateRequestType: dto.estimateRequestType ?? '',
        projectManagementType: dto.projectManagementType ?? '',
        cpdepPhase: dto.cpdepPhase ?? '',
        designProgress: dto.designProgress ?? '',
        anticipatedCompletionDate: dto.projectCompletionDate?.split('T')[0] ?? '',
        preliminaryCostDeadline: dto.deadlineDate?.split('T')[0] ?? '',
        area: dto.area ?? '',
        unit: dto.unit ?? '',
        ibp: dto.ibpYear ?? '',
        taYear: dto.turnaroundYear ?? '',
      });
      this.originalPmtValue.set(dto.projectManagementType ?? '');

      const estimateYear = dto.estimateYear;
      const sequenceNumber = dto.sequenceNumber;
      const hasSearchParams =
        Number.isInteger(estimateYear)
        && sequenceNumber != null
        && String(sequenceNumber).trim() !== ''
        && String(sequenceNumber).toLowerCase() !== 'null';

      if (!hasSearchParams) {
        return;
      }

      const dropdownProjects = await firstValueFrom(
        this.projectsApi.getCustomDropdownProjects(estimateYear, String(sequenceNumber).trim()),
      );

      this.projectTitleOptions = this.extractDropdownValues(
        dropdownProjects,
        'projectTitle',
        EMPTY_OPTIONS,
      );
      this.estimateTitleOptions = this.extractDropdownValues(
        dropdownProjects,
        'estimateTitle',
        EMPTY_OPTIONS,
      );
      this.projectNumberOptions = this.extractDropdownValues(
        dropdownProjects,
        'projectNumber',
        EMPTY_OPTIONS,
      );
      this.projectCodeOptions = this.extractDropdownValues(
        dropdownProjects,
        'projectCode',
        EMPTY_OPTIONS,
      );
    } catch (error) {
      // Log load errors for debugging; keep options empty on failure
      this.logger.warn('Failed to load project dropdown options', error);
    }
  }

  private async loadCpdepPhases(): Promise<void> {
    try {
      const phases = await firstValueFrom(this.dictionariesApi.getCpdepPhases());
      this.cpdepPhases = phases;
    } catch (error) {
      console.error(error);
      this.cpdepPhases = [];
    }
  }

  private async loadDesignProgresses(): Promise<void> {
    try {
      const progresses = await firstValueFrom(this.dictionariesApi.getDesignProgresses());
      this.designProgressOptions = progresses;
    } catch (error) {
      console.error(error);
      this.designProgressOptions = [];
    }
  }

  private async loadEstimateRequestTypes(): Promise<void> {
    try {
      const types = await firstValueFrom(this.dictionariesApi.getEstimateRequestTypes());
      this.estimateRequestTypes = types;
    } catch (error) {
      console.error(error);
      this.estimateRequestTypes = [];
    }
  }

  private async loadIbpYears(): Promise<void> {
    try {
      const years = await firstValueFrom(this.dictionariesApi.getIbpYears());
      this.ibpOptions = years;
    } catch (error) {
      console.error(error);
      this.ibpOptions = [];
    }
  }

  private async loadProjectManagementTypes(): Promise<void> {
    try {
      const types = await firstValueFrom(this.dictionariesApi.getProjectManagementTypes());
      this.projectManagementTypes = types;
    } catch (error) {
      console.error(error);
      this.projectManagementTypes = [];
    }
  }

  private async loadTurnaroundYears(): Promise<void> {
    try {
      const years = await firstValueFrom(this.dictionariesApi.getTurnaroundYears());
      this.taYearOptions = years;
    } catch (error) {
      console.error(error);
      this.taYearOptions = [];
    }
  }

  private async loadUnits(): Promise<void> {
    try {
      const units = await firstValueFrom(this.dictionariesApi.getUnits());
      this.unitOptions = units;
    } catch (error) {
      console.error(error);
      this.unitOptions = [];
    }
  }

  private async loadAreas(): Promise<void> {
    try {
      const areas = await firstValueFrom(this.dictionariesApi.getAreas());
      this.areaOptions = areas;
    } catch (error) {
      console.error(error);
      this.areaOptions = [];
    }
  }

  protected startEdit(field: string, event: Event): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    event.stopPropagation();
    this.editingFields.update(m => ({ ...m, [field]: true }));
    this.focusEditableInput(field);
  }

  protected createNewValue(field: string, event: Event): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    event.stopPropagation();
    this.createNewValueFromDropdown(field);
  }

  protected handleTopDropdownChange(field: string, value: string): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    if (value === '+ Create New') {
      this.createNewValueFromDropdown(field);
      return;
    }

    this.updateField(field, value);
  }

  private createNewValueFromDropdown(field: string): void {
    this.formData.update(f => ({ ...f, [field]: '' }));
    this.editingFields.update(m => ({ ...m, [field]: true }));
    this.focusEditableInput(field);
  }

  protected endEdit(field: string): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    this.editingFields.update(m => ({ ...m, [field]: false }));
  }

  protected updateField(field: string, value: string): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    if (field === 'projectManagementType') {
      // Auto-fill CPDEP Phase and Design Progress from backend stage data
      const cpdepPhase = this.stageService.getCpdepPhase(value);
      const designProgress = this.stageService.getDesignProgress(value);
      
      this.formData.update(f => ({
        ...f,
        projectManagementType: value,
        cpdepPhase: cpdepPhase || '',
        designProgress: designProgress || '',
      }));
    } else {
      this.formData.update(f => ({ ...f, [field]: value }));
    }
  }

  protected openAutocomplete(field: string, trigger: MatAutocompleteTrigger): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    this.showAllAutocomplete.update(current => ({ ...current, [field]: true }));
    trigger.openPanel();
  }

  protected closeAutocomplete(field: string): void {
    setTimeout(() => {
      this.showAllAutocomplete.update(current => ({ ...current, [field]: false }));
    }, 120);
  }

  protected onAutocompleteValueChange(field: string, value: string): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    this.showAllAutocomplete.update(current => ({ ...current, [field]: false }));
    this.handleTopDropdownChange(field, value);
  }

  protected onSelectValueChange(field: string, value: string): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    this.showAllAutocomplete.update(current => ({ ...current, [field]: false }));
    this.updateField(field, value);
  }

  protected onProjectManagementTypeChange(value: string): void {
    if (this.isProjectManagementTypeReadonly()) {
      return;
    }
    this.showAllAutocomplete.update(current => ({ ...current, projectManagementType: false }));
    this.updateField('projectManagementType', value);
  }

  protected cancelPmtChange(): void {
    this.autoDownloadAnswers.set(false);
    this.showPmtConfirmModal.set(false);
    this.pmtPendingCallback = null;
  }

  protected async confirmPmtChange(): Promise<void> {
    if (this.autoDownloadAnswers()) {
      await this.downloadAnswersAsCsv();
    }
    this.originalPmtValue.set(this.formData().projectManagementType);
    this.showPmtConfirmModal.set(false);
    this.autoDownloadAnswers.set(false);
    const callback = this.pmtPendingCallback;
    this.pmtPendingCallback = null;
    if (callback) {
      await callback();
    }
  }

  private async downloadAnswersAsCsv(): Promise<void> {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    this.isDownloadingAnswers.set(true);
    try {
      const response = await firstValueFrom(this.questionnaireApi.exportAnswersByProject(projectId));
      const blob = response.body;
      if (!blob) {
        return;
      }
      const fileName = this.getExportFileName(response.headers.get('content-disposition'), projectId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download questionnaire answers:', error);
      const message = await this.extractBlobErrorMessage(error);
      this.notifications.showError(message);
    } finally {
      this.isDownloadingAnswers.set(false);
    }
  }

  private getExportFileName(contentDisposition: string | null, projectId: number): string {
    if (!contentDisposition) {
      return `questionnaire-answers-${projectId}.xlsx`;
    }
    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
      try {
        return decodeURIComponent(utfMatch[1]);
      } catch {
        return utfMatch[1];
      }
    }
    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1] || `questionnaire-answers-${projectId}.xlsx`;
  }

  private async extractBlobErrorMessage(error: unknown): Promise<string> {
    const fallback = 'Failed to download questionnaire answers.';
    if (error == null || typeof error !== 'object' || !('error' in error)) {
      return fallback;
    }
    const blob = (error as { error?: unknown }).error;
    if (!(blob instanceof Blob)) {
      return fallback;
    }
    try {
      const text = await blob.text();
      const trimmed = text.trim();
      if (!trimmed) {
        return fallback;
      }
      try {
        const parsed = JSON.parse(trimmed) as Record<string, unknown>;
        const msg = parsed['message'] ?? parsed['title'] ?? parsed['detail'];
        return typeof msg === 'string' && msg.trim() ? msg.trim() : trimmed;
      } catch {
        return trimmed;
      }
    } catch {
      return fallback;
    }
  }

  protected openDatePicker(event: Event): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    const input = event.target as HTMLInputElement | null;
    if (!input || input.type !== 'date') {
      return;
    }

    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      try {
        pickerInput.showPicker();
      } catch (error) {
        // Ignore when browser blocks programmatic opening (some browsers don't support showPicker).
        this.logger.debug('Date picker not available in this browser', error);
      }
    }
  }

  protected isStepClickable(step: FlowStep): boolean {
    return !!(step.completed && step.route);
  }

  protected async goToStep(step: FlowStep): Promise<void> {
    if (!this.isStepClickable(step)) {
      return;
    }

    if (step.route === '/questionnaire' && !this.isSubmittedStatus()) {
      if (this.hasPmtChanged()) {
        this.autoDownloadAnswers.set(true);
        this.pmtPendingCallback = () => this.goToStep(step);
        this.showPmtConfirmModal.set(true);
        return;
      }

      this.detailsAttempted.set(true);

      if (!this.hasRequiredDetails()) {
        return;
      }

      this.syncToState();
      const saved = await this.createOrUpdateProject();
      if (!saved) {
        return;
      }
    }

    const id = this.projectId();
    const queryParams = id ? { projectId: id } : undefined;
    this.router.navigate([step.route!], queryParams ? { queryParams } : {});
  }

  protected getAutocompleteOptions(options: readonly string[], field: string, value: string): readonly string[] {
    if (this.showAllAutocomplete()[field]) {
      return options;
    }

    const query = value.trim().toLowerCase();
    if (!query) {
      return options;
    }

    // If current value is already a selected option, show the full list on reopen.
    if (options.some(option => option.toLowerCase() === query)) {
      return options;
    }

    return options.filter(option => option.toLowerCase().includes(query));
  }

  protected handleSelectChange(field: string, value: string): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    if (value === '__create_new__') {
      this.formData.update(f => ({ ...f, [field]: '' }));
      this.editingFields.update(m => ({ ...m, [field]: true }));
      this.focusEditableInput(field);
    } else {
      this.updateField(field, value);
    }
  }

  private focusEditableInput(field: string): void {
    requestAnimationFrame(() => {
      const input = document.querySelector<HTMLInputElement>(`input[name="${field}"]`);
      if (!input) {
        return;
      }

      input.focus();
      const valueLength = input.value.length;
      input.setSelectionRange(valueLength, valueLength);
    });
  }

  protected async search(): Promise<void> {
    if (this.isSubmittedStatus()) {
      return;
    }

    const { estimateYear, sequenceNumber } = this.formData();
    this.searchAttempted.set(true);
    this.searchError.set(null);
    this.searchNotFound.set(false);

    if (!estimateYear.trim() || !sequenceNumber.trim()) {
      return;
    }

    try {
      const year = parseInt(estimateYear.trim(), 10);
      const seq = sequenceNumber.trim();

      if (isNaN(year) || !seq) {
        return;
      }

      const projectBySearch = await firstValueFrom(this.projectsApi.searchProject(year, seq));
      const projects = await firstValueFrom(this.projectsApi.getCustomDropdownProjects(year, seq));
      const project = projects?.[0];

      // Check if project is found
      if (!project && !projectBySearch) {
        this.searchNotFound.set(true);
        return;
      }

      this.projectTitleOptions = this.extractDropdownValues(projects, 'projectTitle', EMPTY_OPTIONS);
      this.estimateTitleOptions = this.extractDropdownValues(projects, 'estimateTitle', EMPTY_OPTIONS);
      this.projectNumberOptions = this.extractDropdownValues(projects, 'projectNumber', EMPTY_OPTIONS);
      this.projectCodeOptions = this.extractDropdownValues(projects, 'projectCode', EMPTY_OPTIONS);

      const projectTitle = projectBySearch?.projectTitle ?? this.projectTitleOptions[0] ?? '';
      const estimateTitle = projectBySearch?.estimateTitle ?? this.estimateTitleOptions[0] ?? '';
      const projectNumber = projectBySearch?.projectNumber ?? this.projectNumberOptions[0] ?? '';
      const projectCode = projectBySearch?.projectCode ?? this.projectCodeOptions[0] ?? '';
      const estimateNumber = projectBySearch?.estimateNumber?.trim() ?? '';

      this.formData.set({
        ...this.formData(),
        projectTitle,
        estimateTitle,
        projectNumber,
        projectCode,
        sequenceTitle: estimateNumber || (project?.estimateYear && seq ? `${project.estimateYear}-${seq}` : ''),
      });
      this.syncToState();
      this.searchNotFound.set(false);
    } catch (error) {
      console.error('Search failed:', error);
      this.searchError.set('Error in service. Please try later');
    }
  }

  protected goBack(): void {
    this.syncToState();
    const id = this.projectId();
    const queryParams = id ? { projectId: id } : undefined;
    this.router.navigate(['/project-team'], queryParams ? { queryParams } : {});
  }

  protected async saveDraft(): Promise<void> {
    if (this.isSubmittedStatus()) {
      return;
    }

    if (this.hasPmtChanged()) {
      this.autoDownloadAnswers.set(true);
      this.pmtPendingCallback = () => this.saveDraft();
      this.showPmtConfirmModal.set(true);
      return;
    }

    this.detailsAttempted.set(true);

    if (!this.hasRequiredDetails()) {
      return;
    }

    this.syncToState();
    const saved = await this.createOrUpdateProject();
    if (!saved) {
      return;
    }

    const createdProjectId = this.projectId();
    if (!createdProjectId) {
      this.notifications.showError('Draft was not saved because project ID was not returned by backend.');
      return;
    }

    this.router.navigate(['/view-request'], { queryParams: { projectId: createdProjectId } });
  }

  protected async save(): Promise<void> {
    if (this.isSubmittedStatus()) {
      return;
    }

    if (this.hasPmtChanged()) {
      this.autoDownloadAnswers.set(true);
      this.pmtPendingCallback = () => this.save();
      this.showPmtConfirmModal.set(true);
      return;
    }

    this.detailsAttempted.set(true);

    if (!this.hasRequiredDetails()) {
      return;
    }

    if (!this.projectId()) {
      return;
    }

    this.syncToState();
    const saved = await this.createOrUpdateProject();
    if (!saved) {
      return;
    }
  }

  protected async goToQuestionnaire(): Promise<void> {
    if (this.isSubmittedStatus()) {
      const queryParams: { projectId?: number | null } = {};

      if (this.projectId()) {
        queryParams.projectId = this.projectId();
      }

      this.router.navigate(['/questionnaire'], { queryParams });
      return;
    }

    if (this.hasPmtChanged()) {
      this.autoDownloadAnswers.set(true);
      this.pmtPendingCallback = () => this.goToQuestionnaire();
      this.showPmtConfirmModal.set(true);
      return;
    }

    this.detailsAttempted.set(true);

    if (!this.hasRequiredDetails()) {
      return;
    }

    this.syncToState();
    const saved = await this.createOrUpdateProject();
    if (!saved) {
      return;
    }

    this.state.markQuestionnaireVisited();
    this.steps.set(getExistingProjectSteps(true, this.state.flowProgress().responseVisited, this.canAccessResponse()));

    const queryParams: { projectId?: number | null } = {};
    if (this.projectId()) {
      queryParams.projectId = this.projectId();
    }

    this.router.navigate(['/questionnaire'], { queryParams });
  }

  private async createOrUpdateProject(): Promise<boolean> {
    const existingId = this.projectId();
    if (existingId) {
      return this.updateProjectOnApi(existingId);
    } else {
      return this.postProjectToApi();
    }
  }

  private async updateProjectOnApi(id: number): Promise<boolean> {
    const f = this.formData();
    const draft = this.state.draftForm();
    const dictionaryIds = await this.resolveProjectDictionaryIds(f);
    if (!dictionaryIds) {
      return false;
    }

    const command: UpdateProjectCommand = {
      projectTeam: draft.projectTeam,
      afeNumber: draft.afeNumber,
      projectTitle: f.projectTitle,
      estimateTitle: f.estimateTitle,
      projectNumber: f.projectNumber,
      projectCode: f.projectCode,
      estimateRequestTypeId: dictionaryIds.estimateRequestTypeId,
      projectManagementTypeId: dictionaryIds.projectManagementTypeId,
      cpdepPhaseId: dictionaryIds.cpdepPhaseId,
      designProgressId: dictionaryIds.designProgressId,
      projectCompletionDate: f.anticipatedCompletionDate
        ? new Date(f.anticipatedCompletionDate).toISOString()
        : '',
      deadlineDate: f.preliminaryCostDeadline
        ? new Date(f.preliminaryCostDeadline).toISOString()
        : '',
      areaId: dictionaryIds.areaId,
      unitId: dictionaryIds.unitId,
      ibpYearId: dictionaryIds.ibpYearId,
      turnaroundYearId: dictionaryIds.turnaroundYearId,
    };
    try {
      await firstValueFrom(this.projectsApi.updateProject(id, command));
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  private async postProjectToApi(): Promise<boolean> {
    const f = this.formData();
    const draft = this.state.draftForm();
    const dictionaryIds = await this.resolveProjectDictionaryIds(f);
    if (!dictionaryIds) {
      return false;
    }

    try {
      const project = await firstValueFrom(
        this.projectsApi.createExistingProject({
          estimateYear: Number(f.estimateYear),
          sequenceNumber: f.sequenceNumber,
          projectTeam: draft.projectTeam,
          afeNumber: draft.afeNumber,
          projectTitle: f.projectTitle,
          estimateTitle: f.estimateTitle,
          projectNumber: f.projectNumber,
          projectCode: f.projectCode,
          estimateRequestTypeId: dictionaryIds.estimateRequestTypeId,
          projectManagementTypeId: dictionaryIds.projectManagementTypeId,
          cpdepPhaseId: dictionaryIds.cpdepPhaseId,
          designProgressId: dictionaryIds.designProgressId,
          projectCompletionDate: f.anticipatedCompletionDate
            ? new Date(f.anticipatedCompletionDate).toISOString()
            : '',
          deadlineDate: f.preliminaryCostDeadline
            ? new Date(f.preliminaryCostDeadline).toISOString()
            : '',
          areaId: dictionaryIds.areaId,
          unitId: dictionaryIds.unitId,
          ibpYearId: dictionaryIds.ibpYearId,
          turnaroundYearId: dictionaryIds.turnaroundYearId,
        }),
      );
      let createdId = 0;
      if (typeof project === 'number') {
        createdId = project;
      } else if (project && typeof project === 'object' && 'id' in project) {
        createdId = Number((project as { id: number }).id);
      }

      if (!Number.isInteger(createdId) || createdId <= 0) {
        this.notifications.showError('Draft was not created: backend did not return a valid project ID.');
        return false;
      }

      this.projectId.set(createdId);
      return true;
    } catch (error) {
      console.error('postProjectToApi error:', error);
      return false;
    }
  }

  private hasRequiredDetails(): boolean {
    const f = this.formData();

    return !!(
      f.projectTitle.trim() &&
      f.estimateTitle.trim() &&
      f.projectNumber.trim() &&
      f.estimateRequestType &&
      f.projectManagementType &&
      f.designProgress &&
      f.unit &&
      f.anticipatedCompletionDate &&
      f.area &&
      f.cpdepPhase &&
      f.preliminaryCostDeadline &&
      f.ibp &&
      f.taYear
    );
  }

  private extractDropdownValues(
    projects: readonly ProjectCustomDropdownDto[],
    field: 'projectTitle' | 'estimateTitle' | 'projectNumber' | 'projectCode',
    fallback: readonly string[],
  ): string[] {
    const values = projects
      .map(project => project[field]?.trim() ?? '')
      .filter((value): value is string => !!value);

    const uniqueValues = Array.from(new Set(values));
    return uniqueValues.length ? uniqueValues : [...fallback];
  }

  private syncToState(): void {
    const f = this.formData();
    this.state.draftForm.update(d => ({
      ...d,
      estimateYear: f.estimateYear,
      sequenceNumber: f.sequenceNumber,
      estimateTitle: f.estimateTitle,
      sequenceTitle: f.sequenceTitle,
      projectNumber: f.projectNumber,
      projectCode: f.projectCode,
      estimateRequestType: f.estimateRequestType,
      projectManagementType: f.projectManagementType,
      cpdepPhase: f.cpdepPhase,
      projectTitle: f.projectTitle,
      designProgress: f.designProgress,
      anticipatedCompletionDate: f.anticipatedCompletionDate,
      area: f.area,
      unit: f.unit,
      preliminaryCostDeadline: f.preliminaryCostDeadline,
      ibp: f.ibp,
      taYear: f.taYear,
    }));
  }

  private async resolveProjectDictionaryIds(f: ReturnType<ExistingProjectComponent['formData']>): Promise<{
    estimateRequestTypeId: number;
    projectManagementTypeId: number;
    cpdepPhaseId: number;
    designProgressId: number;
    areaId: number;
    unitId: number;
    ibpYearId: number;
    turnaroundYearId: number;
  } | null> {
    const [estimateRequestTypes, projectManagementTypes, cpdepPhases, designProgresses, areas, units, ibpYears, turnaroundYears] = await Promise.all([
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('EstimateRequestTypes')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('ProjectManagementTypes')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('CpdepPhases')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('DesignProgresses')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('Areas')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('Units')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('IbpYears')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('TurnaroundYears')),
    ]);

    const ids = {
      estimateRequestTypeId: this.dictionariesApi.findDictionaryId(estimateRequestTypes, f.estimateRequestType),
      projectManagementTypeId: this.dictionariesApi.findDictionaryId(projectManagementTypes, f.projectManagementType),
      cpdepPhaseId: this.dictionariesApi.findDictionaryId(cpdepPhases, f.cpdepPhase),
      designProgressId: this.dictionariesApi.findDictionaryId(designProgresses, f.designProgress),
      areaId: this.dictionariesApi.findDictionaryId(areas, f.area),
      unitId: this.dictionariesApi.findDictionaryId(units, f.unit),
      ibpYearId: this.dictionariesApi.findDictionaryId(ibpYears, f.ibp),
      turnaroundYearId: this.dictionariesApi.findDictionaryId(turnaroundYears, f.taYear),
    };

    if (Object.values(ids).some(id => id <= 0)) {
      this.notifications.showError('One or more dictionary values are outdated. Please reselect dropdown values and try again.');
      return null;
    }

    return ids;
  }

}

